<template>
  <figure class="figure">
    <div v-bind:class="containerClass" ref="playerCont">
      <div class="videoPlayer" ref="player" tabindex="-1"></div>
      <transition name="fade">
        <button
          v-bind:style="{ backgroundImage: 'url(' + posterSrc + ')' }"
          v-on:click="videoPlay"
          v-if="!playing"
          v-bind:title="videoTitle"
        >
          <span>{{ actionText }}</span>
        </button>
        <!-- https://codepen.io/pixelthing/pen/zGZKaQ -->
      </transition>
    </div>
    <figcaption class="figure__caption">{{ videoCredit }}</figcaption>
  </figure>
</template>

<script>
export default {
  name: "videoPosterPlayer",
  props: {
    posterSrc: {
      type: String,
    },
    videoId: {
      type: String,
      required: true,
    },
    playerVars: {
      type: Object,
      default: () => ({}),
    },
    playText: {
      type: String,
      default: "Play Video",
    },
    videoCredit: {
      type: String,
    },
    aspectRatio: {
      type: String,
      default: "16x9",
    },
  },
  data() {
    return {
      player: {},
      iFrameActive: false,
      playerReady: false,
      playing: false,
      iframe: null,
    };
  },
  mounted() {
    //assign component function to run with youtube api is loaded.
    window.onYouTubeIframeAPIReady = () => this.initVideo();

    //add youtube API script asynchronously
    let src = "https://www.youtube.com/iframe_api";
    if (document.querySelector("script[src='" + src + "']")) {
      return;
    }
    let script = document.createElement("script");
    script.setAttribute("src", src);
    script.setAttribute("async", "");
    script.setAttribute("defer", "");
    document.head.appendChild(script);
  },
  computed: {
    containerClass: function () {
      let classes = ["videoWrapper"];
      let aspectRatioClass = "videoWrapper--" + this.aspectRatio;
      classes.push(aspectRatioClass);
      if (this.playerReady) classes.push("videoWrapper--ready");
      return classes;
    },
    videoTitle: function () {
      if (this.playerReady) {
        let videoData = this.player.getVideoData();
        return videoData.title;
      }
      return "Loading video.";
    },
    actionText: function () {
      return this.playerReady ? this.playText : "Loading Video";
    },
  },
  watch: {
    playing: function (value) {
      this.iframe.tabIndex = value ? 0 : -1;
    },
  },
  methods: {
    initVideo() {
      let width = this.$refs.playerCont.clientWidth;
      let height = this.$refs.playerCont.clientHeight;
      this.player = new YT.Player(this.$refs.player, {
        width: width,
        height: height,
        videoId: this.videoId,
        playerVars: this.playerVars,
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
        },
      });
      this.iFrameActive = true;
      this.iframe = this.player.getIframe();
    },
    videoPlay() {
      if (this.playerReady) {
        //triggers the video to play and hides the poster
        this.iframe.focus();
        this.player.playVideo();
        this.playing = true;
      }
      //record google analytics event
    },
    onPlayerReady(event) {
      //set flag that the iframe has loaded the video and it is ready to be played
      this.playerReady = true;
    },
    onPlayerStateChange() {},
  },
};
</script>

<style>
.videoPlayer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
}
</style>
<style lang="scss" scoped>
@import "../../styles/variables";
@import "../../styles/mixins";

$color-button: rgba(255, 255, 255, 0.8);
$color-button-hover: $white;
$shadow: 5px 5px 5px $black;

.videoWrapper {
  position: relative;
  width: 100%;
  height: 0;
  background-color: $base-type-color;

  &--4x3 {
    padding-top: 75%;
  }
  &--16x9 {
    padding-top: 56%;
  }
  &--ready {
    button {
      &:after {
        border-left: 50px solid $color-button;
        border-top: 35px solid transparent;
        border-bottom: 35px solid transparent;
      }
    }
  }
}

button {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  cursor: pointer;
  border: 0;
  outline: none;
  background-position: 50% 50%;
  background-size: 100% 100%;
  background-size: cover;
  background-color: $base-type-color;
  overflow: hidden;
  opacity: 1;
  color: $color-button;
  text-shadow: $shadow;
  font-size: $heading-size--h1;
  font-weight: $bold-weight;
  font-family: $font-family--condensed;
  transition: color 300ms, opacity 800ms, height 0s;
  transition-delay: 0s, 0s, 0s;
  display: flex;
  justify-content: center;
  align-items: center;

  span {
  }

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    margin: -100px 0 0 -25px;
    transition: border-color 300ms;
  }

  &:hover,
  &:focus {
    color: $color-button-hover;
    &:after {
      border-left-color: $color-button-hover;
    }
  }

  @media ($medium-up) {
    font-size: $heading-size--h1 * 1.25;
  }
  @media ($large-up) {
    font-size: $heading-size--h1 * 1.5;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
